import { roleSatisfies } from "@mhj/shared/roles";
import type { UserRole } from "@mhj/shared/types";

/**
 * Roles allowed into the admin panel at all. `field_officer` is deliberately
 * absent — officers work in the empowerment app at `/capture`, not here, and
 * `OfficerCapture` already gates itself (D12 item 1).
 */
export const ADMIN_ROLES = new Set<UserRole>([
  "super_admin",
  "admin",
  "content_editor",
  "scholarship_reviewer",
  "empowerment_reviewer",
]);

export type AdminNavItem = {
  to: string;
  label: string;
  /** Matches <NavLink end>: only match this route exactly, not its children. */
  end?: boolean;
  /**
   * The literal roles the route(s) this page calls were guarded with
   * (`requireRole([...])`, copied as-is) — not the roles that can actually
   * reach it. `roleSatisfies` (shared with the server's own `requireRole`)
   * expands `admin` to whatever it stands in for, so this list only needs
   * to match the route guard, never the composite rule, and the two can't
   * drift apart (D12 item 1).
   */
  roles: UserRole[];
};

export const ADMIN_NAV_ITEMS: AdminNavItem[] = [
  { to: "/admin", label: "Dashboard", end: true, roles: [...ADMIN_ROLES] },
  { to: "/admin/projects", label: "Projects Manager", roles: ["content_editor", "super_admin"] },
  { to: "/admin/scholarships", label: "Scholarships", roles: ["scholarship_reviewer", "super_admin"] },
  { to: "/admin/scholarship-settings", label: "Settings", roles: ["scholarship_reviewer", "super_admin"] },
  { to: "/admin/empowerment", label: "Empowerment", end: true, roles: ["empowerment_reviewer", "super_admin"] },
  { to: "/admin/empowerment/cycles", label: "Empowerment Cycles", roles: ["empowerment_reviewer", "super_admin"] },
  { to: "/admin/empowerment/tracks", label: "Empowerment Tracks", roles: ["empowerment_reviewer", "super_admin"] },
  { to: "/admin/empowerment-settings", label: "Empowerment Settings", roles: ["empowerment_reviewer", "super_admin"] },
  { to: "/admin/curious", label: "Curious Cat", roles: ["content_editor", "super_admin"] },
  { to: "/admin/users", label: "Users & Roles", roles: ["super_admin"] },
];

export function navItemsFor(role: UserRole): AdminNavItem[] {
  return ADMIN_NAV_ITEMS.filter((item) => roleSatisfies(item.roles, role));
}
